import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as companyApi from '../../services/company-api.js';
import type { CompanyBoardColumns, CompanyBoardItem, BoardColumn } from '../../services/company-api.js';
import { createTask, createRoutine, answerRoutineQuestions, toggleRoutine } from '../../services/company-api.js';
import { wsClient } from '../../services/websocket-client.js';
import './cp-company-board-card.js';

interface ColumnConfig {
  key: BoardColumn;
  label: string;
}

const COLUMNS: ColumnConfig[] = [
  { key: 'open', label: 'Offen' },
  { key: 'inProgress', label: 'In Arbeit' },
  { key: 'blocked', label: 'Blockiert' },
  { key: 'done', label: 'Erledigt' },
];

@customElement('cp-company-board')
export class CpCompanyBoard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId = 0;

  @state() private _board: CompanyBoardColumns | null = null;
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _showTaskForm = false;
  @state() private _showRoutineForm = false;
  @state() private _taskTitle = '';
  @state() private _routineName = '';
  @state() private _routineSchedule = '';
  @state() private _routinePrompt = '';
  @state() private _formSubmitting = false;

  private _wsHandler: ((e: Event) => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this._load();

    this._wsHandler = (e: Event) => {
      const msg = (e as CustomEvent<{ type: string; payload?: { companyId?: number } }>).detail;
      if (
        msg.type === 'strategy:state-changed' ||
        msg.type === 'founder-ticket:created' ||
        msg.type === 'founder-ticket:updated' ||
        msg.type === 'founder-ticket:commented' ||
        msg.type === 'founder-ticket:deleted'
      ) {
        void this._load();
      }
    };
    wsClient.addEventListener('ws-message', this._wsHandler);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._wsHandler) {
      wsClient.removeEventListener('ws-message', this._wsHandler);
      this._wsHandler = null;
    }
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('companyId') && this.companyId > 0) {
      void this._load();
    }
  }

  private async _load(): Promise<void> {
    if (this.companyId <= 0) return;
    this._loading = true;
    this._error = '';
    try {
      this._board = await companyApi.getCompanyBoard(this.companyId);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden des Boards';
    } finally {
      this._loading = false;
    }
  }

  private async _handleCreateTask(): Promise<void> {
    if (!this._taskTitle.trim() || this.companyId <= 0) return;
    this._formSubmitting = true;
    try {
      await createTask(this.companyId, { title: this._taskTitle.trim() });
      this._taskTitle = '';
      this._showTaskForm = false;
      void this._load();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Erstellen der Aufgabe';
    } finally {
      this._formSubmitting = false;
    }
  }

  private async _handleCreateRoutine(): Promise<void> {
    if (!this._routineName.trim() || !this._routineSchedule.trim() || !this._routinePrompt.trim() || this.companyId <= 0) return;
    this._formSubmitting = true;
    try {
      await createRoutine(this.companyId, {
        name: this._routineName.trim(),
        schedule: this._routineSchedule.trim(),
        prompt: this._routinePrompt.trim(),
      });
      this._routineName = '';
      this._routineSchedule = '';
      this._routinePrompt = '';
      this._showRoutineForm = false;
      void this._load();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Erstellen der Routine';
    } finally {
      this._formSubmitting = false;
    }
  }

  private async _handleRoutineAnswer(e: CustomEvent<{ cronJobId: number; answers: string }>): Promise<void> {
    const { cronJobId, answers } = e.detail;
    try {
      await answerRoutineQuestions(this.companyId, cronJobId, answers);
      void this._load();
    } catch (err) {
      console.error('Routine answer failed:', err);
    }
  }

  private async _handleRoutineToggle(e: CustomEvent<{ cronJobId: number }>): Promise<void> {
    try {
      await toggleRoutine(this.companyId, e.detail.cronJobId);
      void this._load();
    } catch (err) {
      console.error('Routine toggle failed:', err);
    }
  }

  private _handleCardClick(e: CustomEvent<{ item: CompanyBoardItem }>): void {
    const item = e.detail.item;
    this.dispatchEvent(new CustomEvent('board-item-selected', {
      detail: { item },
      bubbles: true,
      composed: true,
    }));
  }

  /**
   * Build a map of parentStrategyId → child items, across all columns.
   * Returns the set of child item IDs (to exclude from top-level rendering).
   */
  private _getSubItemMap(): { subItemMap: Map<number, CompanyBoardItem[]>; childIds: Set<string> } {
    const subItemMap = new Map<number, CompanyBoardItem[]>();
    const childIds = new Set<string>();

    if (!this._board) return { subItemMap, childIds };

    // Build strategyId → item map to look up parent agents
    const strategyItemMap = new Map<number, CompanyBoardItem>();
    for (const col of Object.values(this._board) as CompanyBoardItem[][]) {
      for (const item of col) {
        const strategyId = item.metadata?.strategyId as number | undefined;
        if (strategyId) strategyItemMap.set(strategyId, item);
      }
    }

    for (const col of Object.values(this._board) as CompanyBoardItem[][]) {
      for (const item of col) {
        const parentId = item.metadata?.parentStrategyId as number | undefined;
        if (!parentId) continue;

        // Only nest as subtask if same agent (self-delegation)
        // Delegations to other agents appear as separate board cards
        const parentItem = strategyItemMap.get(parentId);
        if (parentItem && parentItem.assignedAgent?.id !== item.assignedAgent?.id) continue;

        if (!subItemMap.has(parentId)) subItemMap.set(parentId, []);
        subItemMap.get(parentId)!.push(item);
        childIds.add(item.id);
      }
    }

    return { subItemMap, childIds };
  }

  /**
   * Collect all routine items from every column.
   */
  private _getRoutines(): CompanyBoardItem[] {
    if (!this._board) return [];
    const routines: CompanyBoardItem[] = [];
    for (const col of Object.values(this._board) as CompanyBoardItem[][]) {
      for (const item of col) {
        if (item.type === 'routine') routines.push(item);
      }
    }
    return routines;
  }

  private _renderRoutineStrip() {
    const routines = this._getRoutines();
    const needsInput = routines.filter(r => r.status === 'needs_input');
    const others = routines.filter(r => r.status !== 'needs_input');
    // Show needs_input first, then active/paused
    const sorted = [...needsInput, ...others];

    return html`
      <div class="cp-company-board__routine-strip">
        <div class="cp-company-board__routine-strip-header">
          <div class="cp-company-board__routine-strip-title">
            <span class="cp-company-board__routine-strip-label">Routinen</span>
            ${routines.length > 0 ? html`
              <span class="cp-company-board__routine-strip-count">${routines.length}</span>
            ` : nothing}
          </div>
          <button
            class="cp-company-board__add-btn cp-company-board__add-btn--routine"
            @click=${() => { this._showRoutineForm = true; this._showTaskForm = false; }}
          >+ Routine</button>
        </div>

        ${sorted.length === 0 && !this._showRoutineForm ? html`
          <p class="cp-company-board__routine-strip-empty">Keine Routinen angelegt</p>
        ` : nothing}

        ${this._showRoutineForm ? html`
          <div class="cp-company-board__inline-form cp-company-board__inline-form--routine">
            <input class="cp-company-board__form-input" type="text" placeholder="Name der Routine"
              .value=${this._routineName} @input=${(e: Event) => { this._routineName = (e.target as HTMLInputElement).value; }} />
            <input class="cp-company-board__form-input" type="text" placeholder="Cron-Schedule (z.B. 0 8 * * *)"
              .value=${this._routineSchedule} @input=${(e: Event) => { this._routineSchedule = (e.target as HTMLInputElement).value; }} />
            <textarea class="cp-company-board__form-input" placeholder="Was soll die Routine tun?" rows="2"
              .value=${this._routinePrompt} @input=${(e: Event) => { this._routinePrompt = (e.target as HTMLTextAreaElement).value; }}></textarea>
            <div class="cp-company-board__form-actions">
              <button class="cp-company-board__form-submit" ?disabled=${!this._routineName.trim() || !this._routineSchedule.trim() || !this._routinePrompt.trim() || this._formSubmitting} @click=${() => void this._handleCreateRoutine()}>
                ${this._formSubmitting ? '...' : 'Erstellen'}
              </button>
              <button class="cp-company-board__form-cancel" @click=${() => { this._showRoutineForm = false; this._routineName = ''; this._routineSchedule = ''; this._routinePrompt = ''; }}>Abbrechen</button>
            </div>
          </div>
        ` : nothing}

        ${sorted.length > 0 ? html`
          <div class="cp-company-board__routine-cards">
            ${sorted.map(item => html`
              <cp-company-board-card
                .item=${item}
                .subItems=${[]}
                @board-card-click=${(e: CustomEvent<{ item: CompanyBoardItem }>) => this._handleCardClick(e)}
                @board-card-updated=${() => void this._load()}
                @routine-answer-submit=${(e: CustomEvent<{ cronJobId: number; answers: string }>) => this._handleRoutineAnswer(e)}
                @routine-toggle=${(e: CustomEvent<{ cronJobId: number }>) => this._handleRoutineToggle(e)}
              ></cp-company-board-card>
            `)}
          </div>
        ` : nothing}
      </div>
    `;
  }

  /**
   * Build maps for strategy→delegated task relationships across all columns.
   * Returns: strategyId → { title, total, done } for cross-agent delegated tasks.
   * Also returns: strategyId → parent title for task→strategy breadcrumb.
   */
  private _getDelegationMaps(): {
    delegationStats: Map<number, { title: string; total: number; done: number }>;
    parentTitleMap: Map<number, string>;
  } {
    const delegationStats = new Map<number, { title: string; total: number; done: number }>();
    const parentTitleMap = new Map<number, string>();

    if (!this._board) return { delegationStats, parentTitleMap };

    // First pass: build strategyId → title map for all strategies
    const strategyTitles = new Map<number, string>();
    for (const col of Object.values(this._board) as CompanyBoardItem[][]) {
      for (const item of col) {
        if (item.type === 'strategy') {
          const sid = item.metadata?.strategyId as number | undefined;
          if (sid) strategyTitles.set(sid, item.title);
        }
      }
    }

    // Second pass: count delegated tasks per parent strategy
    for (const col of Object.values(this._board) as CompanyBoardItem[][]) {
      for (const item of col) {
        if (item.type !== 'task') continue;
        const parentId = item.metadata?.parentStrategyId as number | undefined;
        if (!parentId) continue;

        const parentTitle = strategyTitles.get(parentId);
        if (parentTitle) {
          parentTitleMap.set(item.metadata?.strategyId as number, parentTitle);
        }

        if (!delegationStats.has(parentId)) {
          delegationStats.set(parentId, { title: parentTitle ?? '', total: 0, done: 0 });
        }
        const stats = delegationStats.get(parentId)!;
        stats.total++;
        if (item.status === 'completed' || item.status === 'reporting') {
          stats.done++;
        }
      }
    }

    return { delegationStats, parentTitleMap };
  }

  private _renderColumn(config: ColumnConfig) {
    const items = this._board?.[config.key] ?? [];
    const { subItemMap, childIds } = this._getSubItemMap();
    const { delegationStats, parentTitleMap } = this._getDelegationMaps();

    // Filter out child items and routines — routines render in their own strip
    const topLevel = items.filter(item => !childIds.has(item.id) && item.type !== 'routine');

    // Sort: strategies first, then everything else (preserving existing order within groups)
    const TYPE_ORDER: Record<string, number> = { strategy: 0, founder_ticket: 1, specwright: 2, task: 3 };
    const sorted = [...topLevel].sort((a, b) => (TYPE_ORDER[a.type] ?? 2) - (TYPE_ORDER[b.type] ?? 2));

    return html`
      <div class="cp-company-board__column">
        <div class="cp-company-board__column-header cp-company-board__column-header--${config.key}">
          <span class="cp-company-board__column-label">${config.label}</span>
          ${sorted.length > 0 ? html`
            <span class="cp-company-board__column-count">${sorted.length}</span>
          ` : nothing}
        </div>

        <div class="cp-company-board__column-body">
          ${sorted.length === 0 ? html`
            <p class="cp-company-board__column-empty">Keine Eintraege</p>
          ` : nothing}
          ${sorted.map(item => {
            const strategyId = item.metadata?.strategyId as number | undefined;
            const subs = strategyId ? (subItemMap.get(strategyId) ?? []) : [];
            const delegation = strategyId ? delegationStats.get(strategyId) : undefined;
            const parentTitle = strategyId ? (parentTitleMap.get(strategyId) ?? '') : '';
            return html`
              <cp-company-board-card
                .item=${item}
                .subItems=${subs}
                .parentStrategyTitle=${parentTitle}
                .delegatedTaskCount=${delegation?.total ?? 0}
                .delegatedTaskDone=${delegation?.done ?? 0}
                @board-card-click=${(e: CustomEvent<{ item: CompanyBoardItem }>) => this._handleCardClick(e)}
                @board-card-updated=${() => void this._load()}
                @routine-answer-submit=${(e: CustomEvent<{ cronJobId: number; answers: string }>) => this._handleRoutineAnswer(e)}
              ></cp-company-board-card>
            `;
          })}
        </div>
      </div>
    `;
  }

  override render() {
    if (this._loading && !this._board) {
      return html`<div class="cp-company-board__loading">Board wird geladen...</div>`;
    }

    if (this._error) {
      return html`<div class="cp-company-board__error">${this._error}</div>`;
    }

    if (!this._board) return nothing;

    return html`
      ${this._renderRoutineStrip()}

      <div class="cp-company-board__actions">
        ${!this._showTaskForm ? html`
          <button class="cp-company-board__add-btn" @click=${() => { this._showTaskForm = true; }}>+ Aufgabe</button>
        ` : nothing}

        ${this._showTaskForm ? html`
          <div class="cp-company-board__inline-form">
            <input
              class="cp-company-board__form-input"
              type="text"
              placeholder="Aufgabe beschreiben..."
              .value=${this._taskTitle}
              @input=${(e: Event) => { this._taskTitle = (e.target as HTMLInputElement).value; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this._handleCreateTask(); if (e.key === 'Escape') this._showTaskForm = false; }}
            />
            <div class="cp-company-board__form-actions">
              <button class="cp-company-board__form-submit" ?disabled=${!this._taskTitle.trim() || this._formSubmitting} @click=${() => void this._handleCreateTask()}>
                ${this._formSubmitting ? '...' : 'Erstellen'}
              </button>
              <button class="cp-company-board__form-cancel" @click=${() => { this._showTaskForm = false; this._taskTitle = ''; }}>Abbrechen</button>
            </div>
          </div>
        ` : nothing}
      </div>

      <div class="cp-company-board__columns">
        ${COLUMNS.map(col => this._renderColumn(col))}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-board': CpCompanyBoard;
  }
}
